'use client';

import {
  faCheckCircle,
  faClose,
  faExclamationTriangle,
  faHexagonExclamation,
  faInfoCircle,
} from '@fortawesome/pro-regular-svg-icons';
import * as ToastPrimitives from '@radix-ui/react-toast';
import { cva } from 'class-variance-authority';
import { forwardRef } from 'react';

import { Button, ButtonProps, ButtonVariant } from '../Button/Button';
import { cn } from '../utils/cn';
import { Icon, IconDefinition } from '../Icon/Icon';

export const ToastProvider = ToastPrimitives.Provider;

export const ToastViewport = forwardRef<
  React.ElementRef<typeof ToastPrimitives.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Viewport
    ref={ref}
    className={cn(
      'fixed bottom-auto right-0 top-0 z-[999999] flex max-h-screen w-[400px] max-w-full p-4',
      className,
    )}
    {...props}
  />
));

export const ToastActionGroup: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => <div className='mt-3 flex flex-row gap-2'>{children}</div>;

export type ToastActionProps = React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action> &
  Omit<ButtonProps, 'size' | 'theme'> & {
    onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
    target?: string;
  };

export const ToastAction = forwardRef<
  React.ElementRef<typeof ToastPrimitives.Action>,
  ToastActionProps
>(({ className, children, variant, onClick, href, rel, target, altText, ...props }, ref) => (
  <ToastPrimitives.Action
    asChild
    ref={ref}
    onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
    altText={altText}
    {...props}
  >
    <Button
      size='small'
      variant={variant}
      className={className}
      theme='dark'
      href={href}
      rel={rel}
      target={target}
    >
      {children}
    </Button>
  </ToastPrimitives.Action>
));

const ToastClose = forwardRef<
  React.ElementRef<typeof ToastPrimitives.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, onClick, ...props }, ref) => (
  <ToastPrimitives.Close
    ref={ref}
    aria-label='Close'
    className={cn(
      'bg-transparent p-0 text-slate-400 opacity-0 transition-opacity hover:text-slate-100 focus:opacity-100 group-hover:opacity-100',
      className,
    )}
    asChild
    toast-close=''
    onClick={onClick}
    {...props}
  >
    <Button variant='ghost' className='-m-0.5 h-6 w-6 p-0' icon={faClose} />
  </ToastPrimitives.Close>
));

const ToastTitle = forwardRef<
  React.ElementRef<typeof ToastPrimitives.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Title
    ref={ref}
    className={cn('m-0 font-sans text-sm font-semibold leading-5 text-slate-100', className)}
    {...props}
  />
));

const ToastDescription = forwardRef<
  React.ElementRef<typeof ToastPrimitives.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitives.Description
    ref={ref}
    className={cn('m-0 font-sans text-sm font-normal leading-5 text-slate-400', className)}
    {...props}
  />
));

const iconStyles = cva('h-5 w-5', {
  variants: {
    variant: {
      info: 'text-indigo-400',
      error: 'text-red-500',
      success: 'text-green-400',
      warning: 'text-yellow-500',
    },
  },
  defaultVariants: {
    variant: 'info',
  },
});

type ToastRootProps = React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> & {
  icon?: IconDefinition;
  title: string;
  description?: string | React.ReactNode;
  variant?: 'error' | 'success' | 'warning' | 'info';
  onDismiss?: () => void;
};

/**
 * A succinct message that is displayed temporarily.
 * @see {@link https://stylus.design/toast}
 */
export const Toast = forwardRef<React.ElementRef<typeof ToastPrimitives.Root>, ToastRootProps>(
  (
    { className, variant = 'info', icon, children, onDismiss, title, description, ...props },
    ref,
  ) => {
    const defaultIcon = () => {
      switch (variant) {
        case 'error':
          return faHexagonExclamation;
        case 'success':
          return faCheckCircle;
        case 'warning':
          return faExclamationTriangle;
        default:
          return faInfoCircle;
      }
    };

    return (
      <ToastPrimitives.Root
        ref={ref}
        className={cn(
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full shadow-toast group pointer-events-auto relative flex w-[400px] max-w-full justify-between gap-2.5 overflow-hidden rounded-lg bg-slate-800 p-4 transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none',
          className,
        )}
        {...props}
      >
        <Icon
          icon={icon ?? defaultIcon()}
          data-testid='toast-icon'
          className={cn(iconStyles({ variant }), 'mt-0.5 size-4')}
        />
        <div className='flex flex-1 flex-col items-start'>
          {title && <ToastTitle>{title}</ToastTitle>}
          {description && <ToastDescription className='mt-0.5'>{description}</ToastDescription>}
          {children}
        </div>
        <ToastClose onClick={onDismiss} />
      </ToastPrimitives.Root>
    );
  },
);

export type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>;

export type ToastActionElement = React.ReactElement<typeof ToastAction>;
